/* eslint-disable prettier/prettier */
import React, { Suspense, useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CSpinner, useColorModes } from '@coreui/react'
import './scss/style.scss'
import './scss/admin-modern.scss'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Auth Pages - place these files in views/pages/
const Login = React.lazy(() => import('./views/pages/login'))
const ForgotPassword = React.lazy(() => import('./views/pages/forgotpassword'))
const ResetPassword = React.lazy(() => import('./views/pages/resetpassword'))

// Public Status Pages
const AllRides = React.lazy(() => import('./views/status/rides'))
const AllAttractions = React.lazy(() => import('./views/status/attractions'))
const IndividualRide = React.lazy(() => import('./views/status/individual-ride'))
const IndividualAttraction = React.lazy(() => import('./views/status/individual-attraction'))
const DisplayTodoStatus = React.lazy(() => import('./views/status/DisplayTodoStatus'))
const WebView = React.lazy(() => import('./views/status/WebView'))
const FOHScreen = React.lazy(() => import('./views/status/fohscreen'))

// Restaurant Display (Public)
const RestaurantDisplay = React.lazy(() => import('./views/display/RestaurantDisplay'))

// Park Map Display (Public)
const PublicParkMap = React.lazy(() => import('./views/status/PublicParkMap'))

// Private Route wrapper
import PrivateRoute from './components/PrivateRoute'

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const storedTheme = useSelector((state) => state.theme)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0]
    if (theme) {
      setColorMode(theme)
    }

    if (isColorModeSet()) {
      return
    }

    setColorMode(storedTheme)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="pt-3 text-center">
            <CSpinner color="primary" variant="grow" />
          </div>
        }
      >
        <Routes>
          {/* Auth Routes (Public) */}
          <Route exact path="/login" name="Login Page" element={<Login />} />
          <Route exact path="/forgot-password" name="Forgot Password" element={<ForgotPassword />} />
          <Route exact path="/reset-password" name="Reset Password" element={<ResetPassword />} />

          {/* Public Status Routes */}
          <Route exact path="/status/rides" name="AllRides" element={<AllRides />} />
          <Route exact path="/status/attractions" name="AllAttractions" element={<AllAttractions />} />
          <Route exact path="/status/webview" name="WebView" element={<WebView />} />
          <Route exact path="/status/fohscreen" name="FOHScreen" element={<FOHScreen />} />
          
          {/* Department Todo Status Displays */}
          <Route exact path="/status/todos/:department" name="DisplayTodoStatus" element={<DisplayTodoStatus />} />
          
          {/* Individual Displays (Public) */}
          <Route exact path="/rides/:rideId" name="IndividualRide" element={<IndividualRide />} />
          <Route exact path="/attractions/:attractionId" name="IndividualAttraction" element={<IndividualAttraction />} />

          {/* Restaurant Display (Public) */}
          <Route exact path="/display/restaurant/:uniqueId" name="RestaurantDisplay" element={<RestaurantDisplay />} />

          {/* Park Map Display (Public) - Standalone without nav/sidebar */}
          <Route exact path="/status/park-map" name="PublicParkMap" element={<PublicParkMap />} />
          <Route exact path="/status/park-map/:mapId" name="PublicParkMapById" element={<PublicParkMap />} />

          {/* Catch-all route for protected pages */}
          <Route path="*" element={<PrivateRoute><DefaultLayout /></PrivateRoute>} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App